import React, { useContext, useState } from 'react';
import { Modal } from '@fiverr-private/overlay';
import { ComplianceContext } from '../ComplianceProvider';
import { ClassificationQuestion } from '../ClassificationQuestion';
import { reportEventsApi } from '../../biEvents';
import { useSellerConfigData } from '../../api/useSellerConfigData';
import { ClassificationForm, ComplianceType, ComplianceTypes } from './types';
import { ComplianceModalContent } from './ComplianceModalContent';
import { getInfoGridPaymentData } from './helpers';
import InfoGrid from './InfoGrid';

interface CustomModalProps {
    isOpen: boolean;
    onClose: () => void;
    complianceType: ComplianceType;
    requeseteeId: number;
    requeseteeUserName: string;
}

export const CustomModal: React.FC<CustomModalProps> = ({
    isOpen,
    onClose,
    complianceType,
    requeseteeId,
    requeseteeUserName,
}) => {
    const complianceContext = useContext(ComplianceContext);

    if (!complianceContext) {
        throw new Error('ComplianceProvider not defined');
    }

    const [classificationForm, setClassificationForm] = useState<ClassificationForm | undefined>();

    const [step, setStep] = useState<number>(0);

    const {
        isLoading,
        workforceClassificationPackageData,
        backgroundCheckPackageData,
        refetchPackagesData,
        refetchSellerConfigs,
    } = useSellerConfigData(requeseteeId);

    const {
        packageTypeTitle,
        timeEstimation,
        amountInDollars,
        checkedComplianceGrid,
        discountInPercents,
        monthlyCredit,
        currency,
    } = getInfoGridPaymentData(complianceType, backgroundCheckPackageData, workforceClassificationPackageData);

    const infoGrid = (
        <InfoGrid
            requeseteeUserName={requeseteeUserName}
            timeEstimation={timeEstimation}
            monthlyCredit={monthlyCredit?.value === 0 || monthlyCredit?.value ? String(monthlyCredit?.value) : ''}
            price={amountInDollars}
            discountInPercents={discountInPercents}
            currency={currency}
            complianceType={complianceType}
        />
    );

    const nextStep = () => setStep((step) => step + 1);

    const handleModalClose = () => {
        reportEventsApi.onModalClose({
            pageName: complianceContext?.pageName ?? '',
            sellerId: requeseteeId,
            discountInPercents,
            complianceType,
            step,
        });

        onClose();
    };

    const modalContent = (
        <ComplianceModalContent
            infoGrid={infoGrid}
            isLoading={isLoading}
            refetchPackagesData={refetchPackagesData}
            refetchSellerConfigs={refetchSellerConfigs}
            packageTypeTitle={packageTypeTitle}
            checkedComplianceGrid={checkedComplianceGrid}
            discountInPercents={discountInPercents}
            currency={currency}
            complianceType={complianceType}
            requeseteeId={requeseteeId}
            requeseteeUserName={requeseteeUserName}
            classificationForm={classificationForm}
            step={step}
            setStep={setStep}
            isOpen={isOpen}
            onClose={handleModalClose}
        />
    );

    const stepContent =
        complianceType === ComplianceTypes.WORKFORCE_CLASSIFICATION
            ? [
                  <ClassificationQuestion
                      key={'ClassificationQuestion'}
                      defaultValues={classificationForm}
                      sellerId={requeseteeId}
                      isOpen={isOpen}
                      onSubmit={(data) => {
                          reportEventsApi.clickedOnSubmitWorkerClassificationQuestionnaire({
                              pageName: complianceContext?.pageName ?? '',
                              sellerId: requeseteeId,
                          });

                          setClassificationForm(data);

                          nextStep();
                      }}
                      onClose={handleModalClose}
                  />,
                  modalContent,
              ]
            : [modalContent];

    return (
        <Modal isOpen={isOpen} onClose={handleModalClose}>
            {stepContent[step]}
        </Modal>
    );
};

export default CustomModal;
